import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { TNil } from '@app/app.interface';
import { IApiResponse } from '@core/interfaces/api-response.interface';
import { ErrorFormatterService } from '@core/services/error-formatter.service';
import { EventTrackingService } from '@core/services/event-tracking.service';
import { SnackbarService } from '@core/services/snackbar.service';
import { EEventType } from '@enums/event-tracking.enum';
import { ComponentStore } from '@ngrx/component-store';
import { catchError, EMPTY, MonoTypeOperatorFunction, Observable, pipe, switchMap, tap, throwError } from 'rxjs';
import { ICustomer, ICustomerUpdatePayload, TCustomerApi, TCustomerCreate } from './customers.interface';
import { CustomersService } from './customers.service';

const CUSTOMER_NAME_ALREADY_EXISTS_CODE = 'customer_name_already_exists' as const;

interface ICustomersState {
  customer: ICustomer | TNil;
  customers: ICustomer[];
  customersLoading: boolean;
  loading: boolean;
  error: string | null;
}

const initialState: ICustomersState = {
  customer: null,
  customers: [],
  customersLoading: false,
  error: null,
  loading: false,
};

@Injectable()
export class CustomersStore extends ComponentStore<ICustomersState> {
  public readonly customers$ = this.selectSignal((state: ICustomersState) => state.customers);
  public readonly customersLoading$ = this.selectSignal((state: ICustomersState) => state.customersLoading);
  public readonly loading$ = this.selectSignal((state: ICustomersState) => state.loading);
  public readonly error$ = this.selectSignal((state: ICustomersState) => state.error);
  public readonly newCustomer$ = signal<ICustomer | null>(null);
  public readonly updatedCustomer$ = signal<ICustomer | null>(null);
  public readonly customer$ = signal<ICustomer | null>(null);
  // Set by updateCustomerName() below when a rename fails because the name is taken - the details
  // page reads this once its subscribe's error callback fires, to show it inline via mat-error.
  public readonly nameError$ = signal<string | null>(null);

  private readonly handleError = (errorMessage: string): MonoTypeOperatorFunction<unknown> =>
    pipe(
      catchError((error: HttpErrorResponse) => {
        const nameErrorDetail = this.errorFormatterService
          .getErrorDetails(error.error)
          .find((detail) => detail.code === CUSTOMER_NAME_ALREADY_EXISTS_CODE);

        if (nameErrorDetail) {
          const { message } = this.errorFormatterService.getErrorMessage(nameErrorDetail, error.error);
          this.snackbarService.openError(message);
        }

        this.patchState({
          error: errorMessage,
          loading: false,
        });

        return EMPTY;
      }),
    );

  public readonly fetchCustomers = this.effect((trigger$: Observable<void>) => {
    return trigger$.pipe(
      tap(() => this.patchState({ customersLoading: true, error: null, loading: true })),
      switchMap(() =>
        this.customersService
          .getCustomers(new HttpParams().set('limit', '1000').set('order_by', '-created_at').set('joins', 'campaigns'))
          .pipe(
            tap((customers: IApiResponse<TCustomerApi[]>) => {
              const mappedCustomers: ICustomer[] = (customers.data ?? []).map((customer) => ({
                ...customer,
              }));

              this.patchState({ customers: mappedCustomers, customersLoading: false, loading: false });
            }),
            this.handleError('Failed to load customers'),
          ),
      ),
    );
  });

  public readonly fetchCustomer = this.effect((trigger$: Observable<string>) => {
    return trigger$.pipe(
      tap(() => this.patchState({ error: null, loading: true })),
      switchMap((id) =>
        this.customersService.getCustomer(id, new HttpParams().set('joins', 'campaigns')).pipe(
          tap((customer: ICustomer | undefined) => {
            this.customer$.set(customer ?? null);
            this.patchState({ customer, loading: false });
          }),
          catchError(() => {
            this.patchState({ error: 'Failed to load customer', loading: false });
            this.router.navigate(['/customers']);

            return EMPTY;
          }),
        ),
      ),
    );
  });

  public readonly deleteCustomer = this.effect((customerId$: Observable<string>) => {
    return customerId$.pipe(
      tap(() => this.patchState({ error: null, loading: true })),
      switchMap((id) =>
        this.customersService.deleteCustomer(id).pipe(
          tap(() => {
            this.patchState({ loading: false });
            this.fetchCustomers();
            this.eventTrackingService.track(EEventType.customerDeleted, {
              customerId: id,
              source: 'customers_datatable',
            });
          }),
          this.handleError('Failed to delete customer'),
        ),
      ),
    );
  });

  public readonly createCustomer = this.effect((customer$: Observable<TCustomerCreate>) => {
    return customer$.pipe(
      tap(() => this.patchState({ error: null, loading: true })),
      switchMap((customer) =>
        this.customersService.createCustomer(customer).pipe(
          tap((createdCustomer) => {
            this.patchState({
              customers: [...this.get().customers, createdCustomer],
              loading: false,
            });
            this.newCustomer$.set(createdCustomer);
          }),
          this.handleError('Failed to create customer'),
        ),
      ),
    );
  });

  public readonly updateCustomer = this.effect(
    (customer$: Observable<{ id: string; customer: ICustomerUpdatePayload }>) => {
      return customer$.pipe(
        tap(() => this.patchState({ error: null, loading: true })),
        switchMap(({ id, customer }) =>
          this.customersService.updateCustomer(id, customer).pipe(
            tap((updatedCustomer) => {
              this.patchState({
                customers: this.get().customers.map((c) => (c.id === id ? updatedCustomer : c)),
                loading: false,
              });
              this.updatedCustomer$.set(updatedCustomer);
            }),
            this.handleError('Failed to update customer'),
          ),
        ),
      );
    },
  );

  // Used by CustomerDetailsComponent's rename flow. Unlike `updateCustomer` above (a fire-and-forget
  // ComponentStore effect), this returns the Observable directly so the caller gets a plain
  // subscribe with a real success/error callback. On a name conflict it populates nameError$ (read
  // by the details page to show inline via mat-error) instead of the generic snackbar - the
  // interceptor already skips that snackbar for this error code, see NON_SNACKBAR_ERROR_CODES.
  public updateCustomerName(id: string, customer: ICustomerUpdatePayload): Observable<ICustomer> {
    this.nameError$.set(null);

    return this.customersService.updateCustomer(id, customer).pipe(
      tap((updatedCustomer) => {
        this.patchState({
          customers: this.get().customers.map((c) => (c.id === id ? updatedCustomer : c)),
        });
      }),
      catchError((error: HttpErrorResponse) => {
        const nameErrorDetail = this.errorFormatterService
          .getErrorDetails(error.error)
          .find((detail) => detail.code === CUSTOMER_NAME_ALREADY_EXISTS_CODE);

        if (nameErrorDetail) {
          this.nameError$.set(this.errorFormatterService.getErrorMessage(nameErrorDetail, error.error).message);
        }

        return throwError(() => error);
      }),
    );
  }

  constructor(
    private readonly customersService: CustomersService,
    private readonly router: Router,
    private readonly eventTrackingService: EventTrackingService,
    private readonly errorFormatterService: ErrorFormatterService,
    private readonly snackbarService: SnackbarService,
  ) {
    super(initialState);
  }
}
